import React, { useMemo } from "react";
import { Filter, Search, Wallet, X } from "lucide-react";
import PredictNavFoot from "../PredictNavFoot";
import {
  FilterGroup,
  FilterLane,
  FilterRailClear,
  FilterRailHead,
  FilterRailSummary,
} from "../PredictFilterRailParts";
import { BP_RAIL_ESCAPE_LINKS } from "../../../utils/bondPredictNavFooter";
import {
  formatCoinAbbrev,
  formatSignedCoin,
  HOLDINGS_PNL_OPTIONS,
  HOLDINGS_SCOPE_OPTIONS,
  HOLDINGS_SORT_OPTIONS,
  ORDER_SIDE_OPTIONS,
  ORDER_STATUS_FILTER_OPTIONS,
  ORDER_TYPE_OPTIONS,
  ORDERS_SCOPE_OPTIONS,
  ORDERS_SORT_OPTIONS,
  PORTFOLIO_MARKET_STATUS_OPTIONS,
  PORTFOLIO_MARKET_TYPE_OPTIONS,
} from "../../../utils/bondPredictPortfolioHelpers";
import {
  BP_HOLDINGS_FILTER_ICONS,
  BP_ORDERS_FILTER_ICONS,
  BP_PORTFOLIO_TAB_ICONS,
  BP_STATUS_ICONS,
  BP_TYPE_ICONS,
} from "../../../utils/sidebarNavIcons";

const PortfolioFiltersRail = ({
  activeTab,
  onTabChange,
  search,
  onSearchChange,
  holdingsFilters,
  onHoldingsFiltersChange,
  ordersFilters,
  onOrdersFiltersChange,
  categoryOptions,
  positionsCount,
  openOrdersCount,
  resultCount = 0,
  pnl,
  pnlTone,
  invested,
  activeFilterCount,
  onClearFilters,
  compact,
  collapsed,
  onToggleCollapse,
  onClose,
}) => {
  const isHoldings = activeTab === "holdings";
  const iconOnly = collapsed && !compact;
  const patchHoldings = (next) => onHoldingsFiltersChange({ ...holdingsFilters, ...next });
  const patchOrders = (next) => onOrdersFiltersChange({ ...ordersFilters, ...next });

  const summaryStats = useMemo(
    () => [
      {
        label: "P&L",
        value: formatSignedCoin(pnl),
        tone: pnlTone === "gain" ? "gain" : pnlTone === "loss" ? "loss" : "",
      },
      { label: "Invested", value: formatCoinAbbrev(invested) },
      { label: isHoldings ? "Positions" : "Open", value: isHoldings ? positionsCount : openOrdersCount },
      { label: "Showing", value: resultCount },
    ],
    [pnl, pnlTone, invested, isHoldings, positionsCount, openOrdersCount, resultCount]
  );

  const headline =
    openOrdersCount > 0 && !isHoldings ? (
      <>
        <strong className="tabular-nums">{openOrdersCount}</strong> working order
        {openOrdersCount === 1 ? "" : "s"}
      </>
    ) : isHoldings && positionsCount > 0 ? (
      <>
        <strong className="tabular-nums">{positionsCount}</strong> open position
        {positionsCount === 1 ? "" : "s"}
      </>
    ) : (
      "Desk clear"
    );

  return (
    <aside
      className={`bp-predict-nav-rail bp-filter-lane-rail bp-port-filters-rail${
        compact ? " bp-predict-nav-rail-compact" : ""
      }${iconOnly ? " bp-predict-nav-rail-collapsed" : ""}`}
    >
      <FilterRailHead
        kicker="Portfolio desk"
        title="Filters"
        activeCount={activeFilterCount}
        collapsed={collapsed}
        compact={compact}
        onToggleCollapse={onToggleCollapse}
        onClose={onClose}
        brandIcon={Wallet}
      />

      <FilterRailSummary
        stats={summaryStats}
        headline={headline}
        urgent={openOrdersCount > 0 && !isHoldings}
        collapsed={iconOnly}
      />

      {!iconOnly ? (
        <label className="bp-port-nav-search">
          <Search size={12} aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={isHoldings ? "Search holdings…" : "Search orders…"}
            aria-label="Search portfolio"
          />
          {search ? (
            <button
              type="button"
              className="bp-port-nav-search-clear"
              onClick={() => onSearchChange("")}
              aria-label="Clear search"
            >
              <X size={11} />
            </button>
          ) : null}
        </label>
      ) : null}

      <div className="bp-predict-nav-scroll">
        <FilterGroup label="View" collapsed={iconOnly} role="tablist">
          <FilterLane
            role="tab"
            ariaSelected={isHoldings}
            active={isHoldings}
            collapsed={iconOnly}
            onClick={() => onTabChange("holdings")}
            icon={BP_PORTFOLIO_TAB_ICONS.holdings}
            label="Holdings"
            hint="Open positions"
            count={positionsCount}
          />
          <FilterLane
            role="tab"
            ariaSelected={!isHoldings}
            active={!isHoldings}
            collapsed={iconOnly}
            onClick={() => onTabChange("orders")}
            icon={BP_PORTFOLIO_TAB_ICONS.orders}
            label="Orders"
            hint="Working & done"
            count={openOrdersCount}
            urgent={openOrdersCount > 0}
          />
        </FilterGroup>

        {isHoldings ? (
          <>
            <FilterGroup label="Scope" collapsed={iconOnly}>
              {HOLDINGS_SCOPE_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value}
                  collapsed={iconOnly}
                  active={holdingsFilters.scope === opt.value}
                  onClick={() => patchHoldings({ scope: opt.value })}
                  icon={BP_HOLDINGS_FILTER_ICONS[opt.value]}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Market status" collapsed={iconOnly}>
              {PORTFOLIO_MARKET_STATUS_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value || "any-status"}
                  collapsed={iconOnly}
                  active={holdingsFilters.marketStatus === opt.value}
                  onClick={() => patchHoldings({ marketStatus: opt.value })}
                  icon={BP_STATUS_ICONS[opt.value ?? ""] || Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Type" collapsed={iconOnly}>
              {PORTFOLIO_MARKET_TYPE_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value || "any-type"}
                  collapsed={iconOnly}
                  active={holdingsFilters.marketType === opt.value}
                  onClick={() => patchHoldings({ marketType: opt.value })}
                  icon={BP_TYPE_ICONS[opt.value ?? ""] || Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="P&amp;L" collapsed={iconOnly}>
              {HOLDINGS_PNL_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value || "any-pnl"}
                  collapsed={iconOnly}
                  active={holdingsFilters.pnl === opt.value}
                  onClick={() => patchHoldings({ pnl: opt.value })}
                  icon={Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Category" collapsed={iconOnly}>
              {categoryOptions.map((opt) => (
                <FilterLane
                  key={opt.value || "any-cat"}
                  collapsed={iconOnly}
                  active={holdingsFilters.category === opt.value}
                  onClick={() => patchHoldings({ category: opt.value })}
                  icon={Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Sort" collapsed={iconOnly}>
              {HOLDINGS_SORT_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value}
                  collapsed={iconOnly}
                  active={holdingsFilters.sort === opt.value}
                  onClick={() => patchHoldings({ sort: opt.value })}
                  icon={Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>
          </>
        ) : (
          <>
            <FilterGroup label="Scope" collapsed={iconOnly}>
              {ORDERS_SCOPE_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value}
                  collapsed={iconOnly}
                  active={ordersFilters.scope === opt.value}
                  onClick={() => patchOrders({ scope: opt.value })}
                  icon={BP_ORDERS_FILTER_ICONS[opt.value]}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Order status" collapsed={iconOnly}>
              {ORDER_STATUS_FILTER_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value || "any-order-status"}
                  collapsed={iconOnly}
                  active={ordersFilters.orderStatus === opt.value}
                  onClick={() => patchOrders({ orderStatus: opt.value })}
                  icon={Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Side" collapsed={iconOnly}>
              {ORDER_SIDE_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value || "any-side"}
                  collapsed={iconOnly}
                  active={ordersFilters.side === opt.value}
                  onClick={() => patchOrders({ side: opt.value })}
                  icon={Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Order type" collapsed={iconOnly}>
              {ORDER_TYPE_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value || "any-order-type"}
                  collapsed={iconOnly}
                  active={ordersFilters.orderType === opt.value}
                  onClick={() => patchOrders({ orderType: opt.value })}
                  icon={Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Market type" collapsed={iconOnly}>
              {PORTFOLIO_MARKET_TYPE_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value || "any-mtype"}
                  collapsed={iconOnly}
                  active={ordersFilters.marketType === opt.value}
                  onClick={() => patchOrders({ marketType: opt.value })}
                  icon={BP_TYPE_ICONS[opt.value ?? ""] || Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Category" collapsed={iconOnly}>
              {categoryOptions.map((opt) => (
                <FilterLane
                  key={opt.value || "any-cat-o"}
                  collapsed={iconOnly}
                  active={ordersFilters.category === opt.value}
                  onClick={() => patchOrders({ category: opt.value })}
                  icon={Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Sort" collapsed={iconOnly}>
              {ORDERS_SORT_OPTIONS.map((opt) => (
                <FilterLane
                  key={opt.value}
                  collapsed={iconOnly}
                  active={ordersFilters.sort === opt.value}
                  onClick={() => patchOrders({ sort: opt.value })}
                  icon={Filter}
                  label={opt.label}
                  hint={opt.hint}
                />
              ))}
            </FilterGroup>
          </>
        )}
      </div>

      <FilterRailClear
        activeCount={activeFilterCount}
        collapsed={iconOnly}
        onClear={onClearFilters}
      />

      <PredictNavFoot links={BP_RAIL_ESCAPE_LINKS} iconOnly={iconOnly} />
    </aside>
  );
};

export default PortfolioFiltersRail;
